import { useState, type FormEvent } from "react";
import { submitReview } from "@/services/directoryService";
import { ApiError } from "@/services/http";
import FormFeedback from "@/components/base/FormFeedback";

interface ReviewFormProps {
  businessId:   number;
  businessName: string;
  onSubmitted?: (payload: { rating: number; authorName: string; comment: string }) => void;
  onCancel?:    () => void;
}

const HONEYPOT = "website_alt";

const inputClass =
  "h-11 w-full rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";
const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground-600";

export default function ReviewForm({
  businessId,
  businessName,
  onSubmitted,
  onCancel,
}: ReviewFormProps) {
  const [rating,     setRating]     = useState(0);
  const [hover,      setHover]      = useState(0);
  const [authorName, setAuthorName] = useState("");
  const [comment,    setComment]    = useState("");
  const [status,     setStatus]     = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg,   setErrorMsg]   = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMsg("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Anti-spam honeypot — bots that fill it see the same success state, no request is sent.
    const trap = formData.get(HONEYPOT);
    if (typeof trap === "string" && trap.trim().length > 0) {
      setStatus("success");
      return;
    }

    if (rating < 1) { setErrorMsg("Please choose a star rating."); return; }
    if (!authorName.trim()) { setErrorMsg("Please add your name."); return; }

    setStatus("submitting");
    try {
      await submitReview({
        businessId,
        rating,
        authorName: authorName.trim(),
        comment:    comment.trim() || undefined,
      });
      onSubmitted?.({ rating, authorName: authorName.trim(), comment: comment.trim() });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof ApiError ? err.message : "Could not submit your review. Please try again.");
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-lg border border-background-200 bg-background-100 p-5">
        <FormFeedback
          status="success"
          title="Review submitted"
          message={`Thanks for reviewing ${businessName}. Your review is now with our team and will appear once approved.`}
        />
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="mt-4 cursor-pointer whitespace-nowrap text-xs font-semibold text-primary-600 hover:text-primary-700"
          >
            Write another review
          </button>
        )}
      </div>
    );
  }

  const activeStar = hover || rating;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
      <p className="font-heading text-sm font-bold text-foreground-950">
        Share your experience with {businessName}
      </p>

      {/* Star selector */}
      <div>
        <p className={labelClass}>Your rating *</p>
        <div className="flex items-center gap-1" role="radiogroup" aria-label="Star rating">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              role="radio"
              aria-checked={rating === star}
              aria-label={`${star} star${star > 1 ? "s" : ""}`}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
              onClick={() => setRating(star)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md transition-colors hover:bg-background-200"
            >
              <i className={`${star <= activeStar ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-400"} text-2xl`} />
            </button>
          ))}
          {rating > 0 && (
            <span className="ml-2 text-xs font-medium text-foreground-500">{rating} / 5</span>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass} htmlFor="rv-name">Your name *</label>
        <input
          id="rv-name"
          name="authorName"
          type="text"
          required
          maxLength={120}
          value={authorName}
          onChange={(e) => setAuthorName(e.target.value)}
          placeholder="e.g. Suresh Kini"
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass} htmlFor="rv-comment">Your review</label>
        <textarea
          id="rv-comment"
          name="comment"
          rows={4}
          maxLength={500}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Tell others what you liked about this business and your experience..."
          className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
        <p className="mt-1 text-xs text-foreground-500">{comment.length} / 500 characters</p>
      </div>

      {/* Reserved anti-spam field — kept out of view and never required. */}
      <input
        type="text"
        name={HONEYPOT}
        className="field-guard-offscreen"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "error" && (
        <div className="flex items-start gap-2.5 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />{errorMsg}
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-11 cursor-pointer items-center justify-center whitespace-nowrap rounded-lg border border-background-300 px-5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "submitting"
            ? <><i className="ri-loader-4-line animate-spin text-base" /> Submitting…</>
            : <><i className="ri-send-plane-line text-base" /> Submit review</>}
        </button>
      </div>
    </form>
  );
}